import classNames from 'classnames/bind';
import { motion, useAnimation } from 'framer-motion';
import React, { useState, useEffect, useRef } from 'react';

import Heading from 'components/shared/heading';
import {
  LETTERS_COLOR_DELAY,
  ANIMATION_SPEED_FACTOR,
  SLIDE_STATIC_TIME,
  SLIDE_NUMBER_UPDATE_DELAY,
} from 'constants/hero-animation-variables';

import SlideAdidas from './slide-adidas';
import SlideNike from './slide-nike';
import SlideSupreme from './slide-supreme';
import styles from './slider.module.scss';

const cx = classNames.bind(styles);

const SLIDES = ['nike', 'adidas', 'supreme'];

const sleepMs = async (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const delay = (delaySeconds, callback) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      callback();
      resolve();
    }, delaySeconds * 1000 * ANIMATION_SPEED_FACTOR);
  });
};

const Slider = () => {
  const controls = useAnimation();
  const sectionRef = useRef();
  // needed ref here to avoid closures and state to trigger rerendering
  const slideNumber = useRef(0);
  const [, setRenderKey] = useState(0);

  const handleSlideChange = () => {
    slideNumber.current = (slideNumber.current + 1) % SLIDES.length;
    setRenderKey(slideNumber.current);
  };

  useEffect(() => {
    const animationLoop = async () => {
      await Promise.all([
        controls.start('hideNike'),
        controls.start('showAdidas'),
        delay(SLIDE_NUMBER_UPDATE_DELAY, handleSlideChange),
      ]);

      await sleepMs(SLIDE_STATIC_TIME * 1000 * ANIMATION_SPEED_FACTOR);

      await Promise.all([
        controls.start('hideAdidas'),
        controls.start('showSupreme'),
        delay(SLIDE_NUMBER_UPDATE_DELAY, handleSlideChange),
      ]);

      await Promise.all([
        controls.start('hideSupreme'),
        controls.start('showNike'),
        delay(SLIDE_NUMBER_UPDATE_DELAY, handleSlideChange),
      ]);

      await sleepMs(SLIDE_STATIC_TIME * 1000 * ANIMATION_SPEED_FACTOR);

      animationLoop();
    };

    const animationSequence = async () => {
      controls.start('appear');
      await controls.start('showNikeInitial');

      await sleepMs(SLIDE_STATIC_TIME * 1000 * ANIMATION_SPEED_FACTOR);

      animationLoop();
    };

    animationSequence();

    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls]);

  const letterVariants = {
    initial: {
      color: 'rgba(0, 0, 0, 0)',
    },
    appear: (custom) => ({
      color: '#1a1a1a',
      transition: {
        delay: ANIMATION_SPEED_FACTOR * custom * LETTERS_COLOR_DELAY,
        duration: 0,
      },
    }),
  };

  const renderLetter = (letter, index, delay, word) => {
    return (
      <motion.span variants={letterVariants} custom={delay + index} key={`${word}-${index}`}>
        {letter}
      </motion.span>
    );
  };

  const renderTitleWord = (word, numLettersBefore) => {
    return word
      .split('')
      .map((letter, index) => renderLetter(letter, index, numLettersBefore, word));
  };

  return (
    <motion.div
      className={`${cx('container', 'row', 'wrapper')}`}
      initial="initial"
      animate={controls}
      ref={sectionRef}
    >
      <SlideNike />
      <SlideAdidas />
      <SlideSupreme />

      <div className={cx('title-wrapper-top')}>
        <Heading className={cx('title')} tag="h1" size="xxl" theme="primary-inverted">
          {renderTitleWord('BEAT', 0)}
        </Heading>
      </div>
      <div className={cx('title-wrapper-bottom')}>
        <Heading className={cx('title')} tag="h1" size="xxl" theme="primary-inverted">
          {renderTitleWord('THE', 4)}
          <span className={cx('space')}> </span>
          <br className={cx('break')} />
          {renderTitleWord('HYPE', 7)}
        </Heading>
      </div>
      <div className={cx('slide-number')}>
        <span>{`0${slideNumber.current + 1}`}</span>
      </div>
    </motion.div>
  );
};

export default Slider;
